// Google Analytics 4 transport, wired to Google Consent Mode v2.
//
// Replaces the old self-hosted cookieless visitor counter (see ANALYTICS.md).
//
// ─── How consent works here ───────────────────────────────────────────────────
// This is the "advanced" Consent Mode implementation:
//
//   1. Before gtag.js is fetched, we push a `consent: default` of DENIED for
//      every storage type. GA4 therefore sets no cookies and stores nothing on
//      the device until it is told otherwise.
//   2. gtag.js loads anyway. While denied it sends *cookieless* pings — no
//      client ID, no identifiers, nothing written to the device — which Google
//      uses to model the traffic it cannot see. That is why the numbers do not
//      collapse when people decline.
//   3. If the visitor accepts, `consent: update` promotes analytics_storage to
//      granted and GA4 starts behaving normally from that moment on.
//
// Ad storage is hard-denied and never updated: this site runs no advertising
// and has no business asking for it. `ads_data_redaction` additionally strips
// ad identifiers from the pings while ad_storage is denied.
//
// ─── The `arguments` gotcha ──────────────────────────────────────────────────
// `gtag()` must push the real `arguments` object onto dataLayer, not an array.
// Google's tag reads the pushed value as an Arguments object; a plain array is
// silently ignored. This is why `gtag` below is a function declaration using
// `arguments` rather than an arrow function with rest parameters.

import { getStoredConsent, onConsentChange, ConsentChoice } from './consent';

declare global {
    interface Window {
        dataLayer: any[];
    }
}

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Local `npm start` must not pollute the production property — this was a real
// problem with the counter this replaces. Set REACT_APP_GA_DEBUG=true to send
// for real from a dev build when you genuinely need to verify the wiring.
const IS_ENABLED_ENVIRONMENT =
    process.env.NODE_ENV === 'production' || process.env.REACT_APP_GA_DEBUG === 'true';

const isConfigured = Boolean(MEASUREMENT_ID) && IS_ENABLED_ENVIRONMENT;

// React StrictMode double-invokes effects in development, and the router may
// remount. Loading gtag.js twice would double-count everything.
let hasInitialised = false;

function gtag(...args: any[]) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
}

/**
 * Set Consent Mode defaults and load gtag.js.
 *
 * Safe to call more than once; only the first call does anything. Call this as
 * early as possible — dataLayer is created synchronously, so any event fired
 * before the script finishes downloading is queued rather than lost.
 */
export function initAnalytics(): void {
    if (hasInitialised || !isConfigured) {
        return;
    }
    hasInitialised = true;

    window.dataLayer = window.dataLayer || [];

    // Everything denied by default. security_storage is granted because it
    // covers strictly-necessary security functions, which need no consent.
    //
    // No `wait_for_update` here: unlike a third-party CMP, our stored decision
    // is read synchronously on the very next line, so there is nothing async to
    // wait for and no reason to delay the first page_view for everyone.
    gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'granted',
    });

    gtag('set', 'ads_data_redaction', true);

    // Returning visitor who already accepted: promote before the first hit, so
    // that page view is attributed correctly rather than arriving cookieless.
    const stored = getStoredConsent();
    if (stored === 'granted') {
        applyConsent('granted');
    }

    gtag('js', new Date());

    // send_page_view: false because this is a single-page app — react-router
    // changes the URL without a document load, so GA4's automatic page_view
    // would only ever fire once. trackPageView() is called on every navigation
    // instead, including the first one.
    gtag('config', MEASUREMENT_ID, { send_page_view: false });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Later decisions (banner clicks, withdrawal via the footer) flow through
    // the same path.
    onConsentChange(applyConsent);
}

function applyConsent(choice: ConsentChoice | null): void {
    if (!isConfigured) {
        return;
    }
    // ad_* are deliberately absent — they stay denied for the life of the page.
    // A withdrawn decision (null) is storage-denied, same as an explicit no.
    gtag('consent', 'update', {
        analytics_storage: choice === 'granted' ? 'granted' : 'denied',
    });
}

/**
 * Record a page view. Called on first load and on every react-router
 * navigation.
 */
export function trackPageView(path: string, title?: string): void {
    if (!isConfigured) {
        logSuppressed('page_view', { page_path: path });
        return;
    }
    gtag('event', 'page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: title || document.title,
    });
}

/**
 * Record a custom event. Prefer the named helpers in events.ts over calling
 * this directly, so every event name lives in one place.
 */
export function trackEvent(name: string, params: Record<string, any> = {}): void {
    if (!isConfigured) {
        logSuppressed(name, params);
        return;
    }
    gtag('event', name, params);
}

function logSuppressed(name: string, params: Record<string, any>): void {
    if (process.env.NODE_ENV !== 'production') {
        // Makes the wiring visible while developing without sending anything.
        console.info('[analytics suppressed]', name, params);
    }
}
